import React, { useState, useEffect, useMemo } from 'react';
import {
    Globe, ShieldAlert, Zap, Cpu, Loader, Terminal, Eye, Trash2, X,
     ShieldCheck, ChevronDown, ChevronUp, List, Bug, FileText, Square, Activity,
    FileCode, Radio, Archive, GitCompare
} from 'lucide-react';
import axios from 'axios';
import axiosClient from '../../api/axiosClient';
import MicroAgentsPanel from './MicroAgentsPanel';

const WebAuditPage = () => {
    const [url, setUrl] = useState('');
    const [reports, setReports] = useState([]);
    const [isScanning, setIsScanning] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [logs, setLogs] = useState([]);
    const [activeScanId, setActiveScanId] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);
    const [expandedFindings, setExpandedFindings] = useState({});
    const [activeTab, setActiveTab] = useState('findings');
    const [compareScanId1, setCompareScanId1] = useState('');
    const [compareScanId2, setCompareScanId2] = useState('');
    const [compareResult, setCompareResult] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [scanStartTimestamp, setScanStartTimestamp] = useState(null);
    const [downloading, setDownloading] = useState(null);
    const [parametersData, setParametersData] = useState([]);
    const [groupBy, setGroupBy] = useState('severity');

    // ── Filter States ──
    const [filterMethod, setFilterMethod] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterSeverity, setFilterSeverity] = useState('all');
    const [filterSensitiveOnly, setFilterSensitiveOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [reportData, setReportData] = useState(null);
    const [indianIpOnly, setIndianIpOnly] = useState(false);
    const [microAgentMode, setMicroAgentMode] = useState(false);
    const [disableTor, setDisableTor] = useState(false);

    const SAFE_ACTIONS_LIST = [
        "PASSIVE_SCAN", "ENDPOINT_ENUM", "FUZZ_INPUT",
        "LOGIC_TEST", "AUTH_BYPASS_TEST", "DESERIALIZATION_PROBE",
        "OPEN_REDIRECT", "LFI", "AUTH_BYPASS",
        "SSRF_TEST", "CORS_MISCONFIG", "PATH_TRAVERSAL",
        "GRAPHQL_INTROSPECTION", "SQL_INJECTION", "JWT_ANALYZER",
        "XSS", "SECRET_LEAKAGE", "HEADER_MANIPULATION", "SUBDOMAIN_TAKEOVER",
        "DETAILED_AUDIT", "UNAUTHORIZED_ACCESS", "IDOR_DEEP", "SSRF",
        "SENSITIVE_DATA_EXPOSURE", "TECH_DETECT", "JS_DEEP_SECRETS",
        "XXE_PROBE", "SOURCEMAP_SECRETS", "IDOR_BRUTEFORCE", "FULL_PROFILER",
        "LOG_SCANNER", "API_FUZZER", "DOT_GIT_SCANNER", "DB_CONNECTION_EXTRACTOR", "ENV_PARSER",
        "JWT_SECRET_BRUTEFORCE", "GIT_DUMP", "CSRF_DETECTOR", "RATE_LIMIT_TESTER", "VERB_TAMPERING", 
        "CACHE_POISON_PROBE", "DOM_XSS_SCANNER", "WS_SECURITY_CHECK",
        "BLIND_SSRF_OOB", "GRAPHQL_ATTACKS", "CSRF_REPLAY", "JWT_ADVANCED", "RACE_CONDITION",
        "NOSQL_INJECTION", "HOST_HEADER_ATTACKS", "SPRING_BOOT_ACTUATOR", "PHP_ADVANCED",
        "FILE_UPLOAD_ATTACK", "HTTP_REQUEST_SMUGGLING", "CRLF_INJECTION", "HTTP_PARAMETER_POLLUTION",
        "SSTI_ADVANCED", "LDAP_INJECTION", "BLIND_XSS_CALLBACK", "WEB_CACHE_DECEPTION",
        "CLIENT_SIDE_PROTO_POLLUTION","CLICKJACKING", "EMAIL_HEADER_INJECTION", "SAML_OAUTH_MISCONFIG",
        "ADVANCED_DESERIALIZATION", "WEAK_SSL_TLS", "SESSION_FIXATION",
        "TRACE_OPTIONS", "DIRECTORY_BRUTEFORCE", "DOCKER_K8S_CONFIGS",
        "CICD_SECRETS", "COMMENT_SECRETS", "USER_ENUMERATION", "SENSITIVE_FILES", "AI_EXPLOIT_CHAIN",
        "ENHANCED_STATIC", "BROWSER_CRAWL", "AI_REVERSE", "DEEP_GRAPHQL", "OPENAPI_PARSE",
        "AI_FUZZER", "WASM_ANALYSIS", "SCHEMA_INFERENCE", "INTELLIGENT_CHAIN",
        "AUTH_TOKEN_BYPASS", "JSESSIONID_ANALYSIS", "OTP_BYPASS", "SESSION_PREDICTION", "PASSWORD_RESET_POISON",
        "OAUTH_REDIRECT_BYPASS", "RFI", "SSI", "MASS_ASSIGNMENT", "BOOLEAN_BLIND_SQLI", "GRAPHQL_ADVANCED", 
        "WEBSOCKET_AUTH", "SAML_WRAPPING", "OAUTH_SCOPE", "FILE_UPLOAD_BYPASS", "DEPENDENCY_CONFUSION",
        "CICD_INJECTION", "SBOM_ANALYSIS", "AI_CODE_ANALYSIS", "LLM_PROMPT_INJECTION", "AI_AGENT_SECURITY", "IAC_SECURITY",
        "K8S_RBAC", "SERVICE_MESH_AUTH", "GRAPHQL_ALIAS_DOS", "GRAPHQL_BATCH", "ADVANCED_LOGIC",
        "CRYPTO_ANALYSIS", "PROTOCOL_FUZZ", "PADDING_ORACLE", "INVALID_CURVE",  "WEAK_RSA_KEY", "SYMBOLIC_EXEC", 
        "MODEL_CHECKING", "AI_FUZZING", "WEBSOCKET_INJECTION", "CVE_LOOKUP", "PARAM_EXTRACTOR", "CLEANUP",
        "AST_ANALYSIS", "BINARY_ANALYSIS", "CSPM_ANALYSIS", "DEEP_403_AUDIT", "DEEP_RE_ATTACK", "ADVANCED_RECON",
        "EXPLOIT_CHAINING", "AI_ENHANCED_FUZZ", "EXTERNAL_TOOLS", "AI_ORCHESTRATOR",
    ];
    
    // ── Use the list for initial state ──
    const [selectedActions, setSelectedActions] = useState(SAFE_ACTIONS_LIST);

    // ── Helper Functions ──
    const getFindingMethod = (f) => {
        return f.proof?.method || f.proof?.request?.method || 'UNKNOWN';
    };

    const getFindingStatus = (f) => {
        return f.proof?.status_code || f.proof?.response_status || f.proof?.status || null;
    };

    const isSensitiveFinding = (f) => {
        const cat = (f.category || '').toLowerCase();
        const title = (f.title || '').toLowerCase();
        const proofStr = JSON.stringify(f.proof || {}).toLowerCase();
        return cat.includes('information_disclosure') ||
            cat.includes('pii_leak') ||
            cat.includes('secret') ||
            title.includes('secret') ||
            title.includes('token') ||
            title.includes('api key') ||
            proofStr.includes('secret') ||
            proofStr.includes('token') ||
            proofStr.includes('api_key') ||
            proofStr.includes('password') ||
            proofStr.includes('credential');
    };

    const applyFilters = (findings) => {
        if (!findings) return [];
        return findings.filter(f => {
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const match = f.title.toLowerCase().includes(q) ||
                              (f.impact || '').toLowerCase().includes(q) ||
                              (f.category || '').toLowerCase().includes(q);
                if (!match) return false;
            }
            if (filterMethod !== 'all') {
                const method = getFindingMethod(f).toUpperCase();
                if (method !== filterMethod) return false;
            }
            if (filterStatus !== 'all') {
                const status = getFindingStatus(f);
                if (status === null || status.toString() !== filterStatus) return false;
            }
            if (filterCategory !== 'all') {
                const cat = (f.category || '').toLowerCase();
                if (cat !== filterCategory.toLowerCase()) return false;
            }
            if (filterSeverity !== 'all') {
                if ((f.severity || '').toLowerCase() !== filterSeverity.toLowerCase()) return false;
            }
            if (filterSensitiveOnly && !isSensitiveFinding(f)) return false;
            return true;
        });
    };

    // ── Computed Values ──
    const allFindings = selectedReport?.findings || [];

    const filteredFindings = useMemo(() => {
        return applyFilters(allFindings);
    }, [allFindings, searchQuery, filterMethod, filterStatus, filterCategory, filterSeverity, filterSensitiveOnly]);

    const availableMethods = useMemo(() => {
        const methods = new Set(allFindings.map(f => getFindingMethod(f).toUpperCase()));
        return ['all', ...methods];
    }, [allFindings]);

    const availableStatuses = useMemo(() => {
        const statuses = new Set(allFindings.map(f => getFindingStatus(f)).filter(s => s !== null).map(String));
        return ['all', ...statuses];
    }, [allFindings]);

    const availableCategories = useMemo(() => {
        const cats = new Set(allFindings.map(f => (f.category || '').toLowerCase()));
        return ['all', ...cats];
    }, [allFindings]);

    const availableSeverities = useMemo(() => {
        const sevs = new Set(allFindings.map(f => (f.severity || '').toLowerCase()));
        return ['all', ...sevs];
    }, [allFindings]);

    const groupFindings = (findings, groupByKey) => {
        const groups = {};
        findings.forEach(f => {
            const key = groupByKey === 'severity' ? f.severity : f.category;
            if (!groups[key]) groups[key] = [];
            groups[key].push(f);
        });
        return groups;
    };

    // ── Effects and API calls ──
    const fetchReports = async () => {
        try {
            const data = await axiosClient.get('/admin/web-audit/reports');
            setReports(Array.isArray(data) ? data : []);
        } catch (e) { console.error("Archive fetch failed"); }
    };

    useEffect(() => {
        fetchReports();
        return () => clearInterval(pollingInterval);
    }, []);

    const [stealthIPs, setStealthIPs] = useState([]);
    useEffect(() => {
        const ipInterval = setInterval(async () => {
            try {
                const res = await axiosClient.get('/admin/stealth/ips');
                setStealthIPs(res.ips || []);
            } catch(e) {}
        }, 3000);
        return () => clearInterval(ipInterval);
    }, []);

    const fetchParameters = async (scanId) => {
        try {
            const response = await axiosClient.get(`/admin/web-audit/parameters/${scanId}`);
            setParametersData(response);
        } catch (e) {
            console.error("Failed to fetch parameters", e);
        }
    };

    const fetchReportData = async (scanId) => {
        try {
            const res = await axiosClient.get(`/admin/web-audit/report/${scanId}`);
            setReportData(res);
        } catch (e) {
            console.error("Failed to fetch report data", e);
        }
    };

    // Inside the existing useEffect for opening modal:
    useEffect(() => {
        if (showDetailsModal && selectedReport) {
            fetchParameters(selectedReport.job?.id || selectedReport.id);
            fetchDeepAudit(selectedReport.job?.id || selectedReport.id);
            fetchReportData(selectedReport.job?.id || selectedReport.id);
        }
    }, [showDetailsModal, selectedReport]);

    useEffect(() => {
        if (showDetailsModal && selectedReport) {
            fetchParameters(selectedReport.job?.id || selectedReport.id);
        }
    }, [showDetailsModal, selectedReport]);

    // ── Comparison, Start, Stop, Delete, View, Expand functions ──
    const compareScans = async () => {
        try {
            const res = await axiosClient.get(`/admin/web-audit/compare?scan_id_1=${compareScanId1}&scan_id_2=${compareScanId2}`);
            setCompareResult(res);
        } catch (e) {
            alert("Comparison failed");
        }
    };

    const startScan = async () => {
        if (!url) return;
        setIsScanning(true);
        setShowLogModal(true);
        setLogs(["[SYSTEM] Initiating Project Oculus...", "[NETWORK] Establishing secure bridge..."]);
        try {
            const response = await axiosClient.post('/admin/web-audit/start', {
                url,
                actions: selectedActions,
                indian_ip_only: indianIpOnly,
                micro_agent_mode: microAgentMode,
                disable_tor: disableTor,
            });
            const { scan_id, message } = response;
            setActiveScanId(scan_id);
            console.log("Oculus Bridge Success:", response);
            setLogs(prev => [message || "Scan started", ...prev]);
            const startTime = Date.now();
            setScanStartTimestamp(startTime);
            setElapsedTime(0);
            const timerInterval = setInterval(() => {
                setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
            const interval = setInterval(async () => {
                try {
                    const reportData = await axiosClient.get(`/admin/web-audit/report/${scan_id}`);
                    const job = reportData.job;
                    if (job) {
                        if (job.summary) {
                            try {
                                const parsedLogs = JSON.parse(job.summary);
                                if (Array.isArray(parsedLogs)) setLogs(parsedLogs.reverse());
                            } catch {}
                        }
                        if (job.status === 'completed' || job.status === 'failed') {
                            clearInterval(interval);
                            clearInterval(timerInterval);
                            setPollingInterval(null);
                            setIsScanning(false);
                            fetchReports();
                            if (job.status === 'completed') {
                                setShowLogModal(false);
                                setSelectedReport(reportData);
                                setShowDetailsModal(true);
                            } else {
                                setLogs(prev => [
                                    "[SYSTEM] Scan failed. Closing window in 5s...",
                                    ...prev
                                ]);
                                setTimeout(() => setShowLogModal(false), 5000);
                            }
                        }
                    }
                } catch (e) {
                    if (e.response?.status === 404) {
                        clearInterval(interval);
                        clearInterval(timerInterval);
                        setPollingInterval(null);
                        setIsScanning(false);
                        setShowLogModal(false);
                        alert("Scan record lost.");
                    }
                }
            }, 2000);
            setPollingInterval(interval);
        } catch (e) {
            console.error(e);
            setIsScanning(false);
            setShowLogModal(false);
            alert(`Oculus Bridge Failure: ${e.response?.data?.detail || e.message}`);
        }
    };

    const stopScan = async (scanId) => {
        if (!window.confirm("Are you sure you want to stop this scan?")) return;
        try {
            await axiosClient.post(`/admin/web-audit/stop/${scanId}`);
            alert("Scan stop requested.");
            fetchReports();
        } catch (e) {
            alert("Failed to stop scan: " + (e.response?.data?.detail || e.message));
        }
    };

    const viewLiveLogs = async (scanId) => {
        try {
            const reportData = await axiosClient.get(`/admin/web-audit/report/${scanId}`);
            const job = reportData.job;
            if (!job) {
                alert("Scan record not found.");
                return;
            }

            // Restore logs from DB summary
            let restoredLogs = [];
            if (job.summary) {
                try {
                    const parsed = JSON.parse(job.summary);
                    if (Array.isArray(parsed)) restoredLogs = parsed.slice().reverse();
                } catch { }
            }
            if (restoredLogs.length === 0) {
                restoredLogs = ["[SYSTEM] Reattaching to live scan..."];
            }
            setLogs(restoredLogs);

            // Restore scan state
            setActiveScanId(scanId);
            setIsScanning(true);
            setShowLogModal(true);

            // Restore elapsed timer
            const startTime = job.started_at ? new Date(job.started_at).getTime() : Date.now();
            setScanStartTimestamp(startTime);
            setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
            const timerInterval = setInterval(() => {
                setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);

            // Restart polling
            const interval = setInterval(async () => {
                try {
                    const rd = await axiosClient.get(`/admin/web-audit/report/${scanId}`);
                    const j = rd.job;
                    if (j) {
                        if (j.summary) {
                            try {
                                const parsedLogs = JSON.parse(j.summary);
                                if (Array.isArray(parsedLogs)) setLogs(parsedLogs.slice().reverse());
                            } catch { }
                        }
                        if (j.status === 'completed' || j.status === 'failed' || j.status === 'stopped') {
                            clearInterval(interval);
                            clearInterval(timerInterval);
                            setPollingInterval(null);
                            setIsScanning(false);
                            fetchReports();
                            if (j.status === 'completed') {
                                setShowLogModal(false);
                                setSelectedReport(rd);
                                setShowDetailsModal(true);
                            } else {
                                setLogs(prev => [`[SYSTEM] Scan ${j.status}. Closing in 5s...`, ...prev]);
                                setTimeout(() => setShowLogModal(false), 5000);
                            }
                        }
                    }
                } catch (e) {
                    if (e.response?.status === 404) {
                        clearInterval(interval);
                        clearInterval(timerInterval);
                        setPollingInterval(null);
                        setIsScanning(false);
                        setShowLogModal(false);
                        alert("Scan record lost.");
                    }
                }
            }, 2000);
            setPollingInterval(interval);
        } catch (e) {
            alert("Failed to reattach to scan: " + (e.response?.data?.detail || e.message));
        }
    };

    const deleteReport = async (id) => {
        if (!window.confirm("Purge this forensic record?")) return;
        try {
            await axiosClient.delete(`/admin/web-audit/reports/${id}`);
            fetchReports();
        } catch (e) { alert("Purge Failed."); }
    };

    // Download client PDF report as blob (bypasses browser popup blockers)
    const downloadClientPdf = async (scanId) => {
        try {
            const resp = await axiosClient.get(
                `/admin/web-audit/client-report/${scanId}/pdf`,
                { responseType: 'blob' }
            );
            const blob = new Blob([resp], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `oculus-report-${scanId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("PDF download failed:", e);
            alert(`PDF download failed: ${e.response?.data?.detail || e.message}`);
        }
    };

    // Open client HTML report in new tab (fetch via axios, then blob URL)
    const viewClientHtml = async (scanId) => {
        try {
            const resp = await axiosClient.get(
                `/admin/web-audit/client-report/${scanId}/html`,
                { responseType: 'blob' }
            );
            const blob = new Blob([resp], { type: 'text/html' });
            const url = window.URL.createObjectURL(blob);
            const newTab = window.open(url, '_blank');
            // Clean up after tab loads (after 60s safety)
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 60000);
            if (!newTab) {
                alert("Popup blocked. Please allow popups for this site.");
            }
        } catch (e) {
            console.error("HTML view failed:", e);
            alert(`Failed to open HTML report: ${e.response?.data?.detail || e.message}`);
        }
    };
    
    // Open Before/After diff report (auto-picks previous scan of same target)
    const viewClientDiff = async (scanId) => {
        try {
            // 1. Find previous scan
            const prevRes = await axiosClient.get(
                `/admin/web-audit/previous-scan/${scanId}`
            );
            const prevId = prevRes?.previous_scan_id;
            if (!prevId) {
                alert(
                    "No previous scan found for this target.\n\n" +
                    "Run at least 2 scans on the same URL to see before/after comparison."
                );
                return;
            }

            // 2. Fetch HTML report with diff
            const resp = await axiosClient.get(
                `/admin/web-audit/client-report/${scanId}/html?diff_from=${prevId}`,
                { responseType: 'blob' }
            );
            const blob = new Blob([resp], { type: 'text/html' });
            const url = window.URL.createObjectURL(blob);
            const newTab = window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            if (!newTab) {
                alert("Popup blocked. Please allow popups for this site.");
            }
        } catch (e) {
            console.error("Diff view failed:", e);
            alert(`Failed to open diff report: ${e.response?.data?.detail || e.message}`);
        }
    };

    const viewReportDetails = async (scanId) => {
        try {
            const data = await axiosClient.get(`/admin/web-audit/report/${scanId}`);
            setSelectedReport(data);
            setShowDetailsModal(true);
            setActiveTab('findings');
            fetchModuleLogs(scanId);
        } catch (e) {
            alert(e.response?.status === 404 ? "Report not found." : "Failed to load full report");
        }
    };

    const toggleExpandFinding = (idx) => {
        setExpandedFindings(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const [moduleLogs, setModuleLogs] = useState([]);
    const fetchModuleLogs = async (scanId) => {
        try {
            const res = await axiosClient.get(`/admin/web-audit/module-logs/${scanId}`);
            setModuleLogs(res.logs || []);
        } catch (e) { console.error("Failed to fetch module logs", e); }
    };

    const computeRisk = (findings) => {
        if (!findings || findings.length === 0) return { score: 0, label: 'none' };
        const sevMap = { critical: 100, high: 80, medium: 60, low: 40, info: 20 };
        const maxSev = Math.max(...findings.map(f => sevMap[f.severity] || 0));
        return { score: maxSev, label: Object.keys(sevMap).find(k => sevMap[k] === maxSev) || 'none' };
    };

    const [deepAuditData, setDeepAuditData] = useState([]);

    const fetchDeepAudit = async (scanId) => {
        try {
            const res = await axiosClient.get(`/admin/web-audit/deep-audit/${scanId}`);
            setDeepAuditData(res);
        } catch (e) { console.error("Failed to fetch deep audit", e); }
    };

    useEffect(() => {
        if (showDetailsModal && selectedReport) {
            fetchDeepAudit(selectedReport.job?.id || selectedReport.id);
        }
    }, [showDetailsModal, selectedReport]);

    return (
        <div className="p-6 lg:p-8 bg-[#02040a] min-h-screen text-slate-200 font-mono relative overflow-x-hidden selection:bg-cyan-500/30">
            {/* Immersive Tech Backdrop Effects */}
            <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[130px] pointer-events-none" />
            <div className="absolute bottom-12 right-12 w-[350px] h-[350px] bg-blue-500/5 rounded-full blur-[90px] pointer-events-none" />

            {/* ─── HUD HEADER & CONTROL DECK ─── */}
            <div className="relative mb-8 bg-slate-950/60 p-6 lg:p-8 rounded-2xl border border-slate-900 shadow-2xl backdrop-blur-md">
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-cyan-500" />
                <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-cyan-500" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-cyan-500" />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-cyan-500" />

                {/* ── Top Row: Logo + Input/Button ── */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                    {/* Logo & Title */}
                    <div className="flex items-center gap-4 shrink-0">
                        <div className="p-3 bg-cyan-950/20 border border-cyan-500/30 rounded-xl shadow-[0_0_15px_rgba(6,182,212,0.15)]">
                            <Globe size={26} className="text-cyan-400 animate-pulse" />
                        </div>
                        <div>
                            <h1 className="text-xl lg:text-2xl font-black tracking-widest text-white uppercase bg-clip-text bg-gradient-to-r from-slate-100 via-slate-300 to-slate-400">
                                PROJECT OCULUS
                            </h1>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
                                <p className="text-[9px] text-cyan-500/80 uppercase tracking-[0.3em] font-bold">
                                    Autonomous Vector Inspection Matrix
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Target Input + Initiate Button */}
                    <div className="flex flex-col sm:flex-row w-full lg:w-xl gap-3 p-1.5 bg-black/60 rounded-xl border border-slate-800 backdrop-blur-xl shadow-inner">
                        <div className="flex items-center px-4 gap-2.5 text-slate-500 w-full sm:w-auto">
                            <Terminal size={14} className="text-cyan-600 shrink-0" />
                            <input
                                className="bg-transparent py-3 outline-none text-cyan-400 text-xs tracking-wider font-semibold min-w-[100px] lg:min-w-[550px] placeholder:text-slate-700 w-full"
                                placeholder="TARGET_NODE_URL // (e.g. domain.com)"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={startScan}
                            disabled={isScanning}
                            className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:hover:bg-cyan-600 px-6 py-3.5 rounded-lg font-bold text-xs uppercase tracking-widest text-black transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.25)] flex items-center justify-center gap-2 shrink-0"
                        >
                            {isScanning ? <Loader className="animate-spin" size={14} /> : <Zap size={14} />}
                            INITIATE
                        </button>
                    </div>
                </div>

                {/* ── Scan Options ── */}
                <div className="mt-4 flex flex-wrap items-center gap-5 text-[10px] text-slate-400">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={indianIpOnly}
                            onChange={(e) => setIndianIpOnly(e.target.checked)}
                            className="w-3.5 h-3.5 accent-cyan-500"
                        />
                        <span className="uppercase tracking-widest font-bold text-cyan-400/90">
                            🇮🇳 Indian IP Only (TOR exit India)
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={microAgentMode}
                            onChange={(e) => setMicroAgentMode(e.target.checked)}
                            className="w-3.5 h-3.5 accent-cyan-500"
                        />
                        <span className="uppercase tracking-widest font-bold text-cyan-400/90">
                            ⚡ Micro Agent (Fast Scan)
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={disableTor}
                            onChange={(e) => setDisableTor(e.target.checked)}
                            className="w-3.5 h-3.5 accent-cyan-500"
                        />
                        <span className="uppercase tracking-widest font-bold text-amber-400/90">
                            🚫 Disable TOR (Direct connection)
                        </span>
                    </label>
                </div>

                {/* ── Attack Vectors (directly below input row) ── */}
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-5">
                        <span className="text-[8px] text-slate-500 uppercase tracking-widest">
                            Engage Attack Vectors
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setSelectedActions([...SAFE_ACTIONS_LIST])}
                                className="text-[9px] bg-cyan-950/30 border border-cyan-500/30 text-cyan-400 px-2 py-0.5 rounded hover:bg-cyan-500/20 transition-colors"
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => setSelectedActions([])}
                                className="text-[9px] bg-slate-800/30 border border-slate-700/50 text-slate-400 px-2 py-0.5 rounded hover:bg-red-500/20 hover:text-red-400 transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 max-h-50 overflow-y-auto custom-scrollbar pr-2">
                        {[
                            "PASSIVE_SCAN", "ENDPOINT_ENUM", "FUZZ_INPUT",
                            "LOGIC_TEST", "AUTH_BYPASS_TEST", "DESERIALIZATION_PROBE",
                            "OPEN_REDIRECT", "LFI", "AUTH_BYPASS",
                            "SSRF_TEST", "CORS_MISCONFIG", "PATH_TRAVERSAL",
                            "GRAPHQL_INTROSPECTION", "SQL_INJECTION", "JWT_ANALYZER",
                            "XSS", "SECRET_LEAKAGE", "HEADER_MANIPULATION", "SUBDOMAIN_TAKEOVER",
                            "DETAILED_AUDIT", "UNAUTHORIZED_ACCESS", "IDOR_DEEP", "SSRF",
                            "SENSITIVE_DATA_EXPOSURE", "TECH_DETECT", "JS_DEEP_SECRETS",
                            "XXE_PROBE", "SOURCEMAP_SECRETS", "IDOR_BRUTEFORCE", "FULL_PROFILER",
                            "LOG_SCANNER", "API_FUZZER", "DOT_GIT_SCANNER", "DB_CONNECTION_EXTRACTOR", "ENV_PARSER",
                            "JWT_SECRET_BRUTEFORCE", "GIT_DUMP", "CSRF_DETECTOR", "RATE_LIMIT_TESTER", "VERB_TAMPERING", 
                            "CACHE_POISON_PROBE", "DOM_XSS_SCANNER", "WS_SECURITY_CHECK",
                            "BLIND_SSRF_OOB", "GRAPHQL_ATTACKS", "CSRF_REPLAY", "JWT_ADVANCED", "RACE_CONDITION",
                            "NOSQL_INJECTION", "HOST_HEADER_ATTACKS", "SPRING_BOOT_ACTUATOR", "PHP_ADVANCED",
                            "FILE_UPLOAD_ATTACK", "HTTP_REQUEST_SMUGGLING", "CRLF_INJECTION", "HTTP_PARAMETER_POLLUTION",
                            "SSTI_ADVANCED", "LDAP_INJECTION", "BLIND_XSS_CALLBACK", "WEB_CACHE_DECEPTION",
                            "CLIENT_SIDE_PROTO_POLLUTION","CLICKJACKING", "EMAIL_HEADER_INJECTION", "SAML_OAUTH_MISCONFIG",
                            "ADVANCED_DESERIALIZATION", "WEAK_SSL_TLS", "SESSION_FIXATION",
                            "TRACE_OPTIONS", "DIRECTORY_BRUTEFORCE", "DOCKER_K8S_CONFIGS",
                            "CICD_SECRETS", "COMMENT_SECRETS", "USER_ENUMERATION", "SENSITIVE_FILES", "AI_EXPLOIT_CHAIN",
                            "ENHANCED_STATIC", "BROWSER_CRAWL", "AI_REVERSE", "DEEP_GRAPHQL", "OPENAPI_PARSE",
                            "AI_FUZZER", "WASM_ANALYSIS", "SCHEMA_INFERENCE", "INTELLIGENT_CHAIN",
                            "AUTH_TOKEN_BYPASS", "JSESSIONID_ANALYSIS", "OTP_BYPASS", "SESSION_PREDICTION", "PASSWORD_RESET_POISON",
                            "OAUTH_REDIRECT_BYPASS", "RFI", "SSI", "MASS_ASSIGNMENT", "BOOLEAN_BLIND_SQLI", "GRAPHQL_ADVANCED", 
                            "WEBSOCKET_AUTH", "SAML_WRAPPING", "OAUTH_SCOPE", "FILE_UPLOAD_BYPASS", "DEPENDENCY_CONFUSION",
                            "CICD_INJECTION", "SBOM_ANALYSIS", "AI_CODE_ANALYSIS", "LLM_PROMPT_INJECTION", "AI_AGENT_SECURITY", "IAC_SECURITY",
                            "K8S_RBAC", "SERVICE_MESH_AUTH", "GRAPHQL_ALIAS_DOS", "GRAPHQL_BATCH", "ADVANCED_LOGIC",
                            "CRYPTO_ANALYSIS", "PROTOCOL_FUZZ", "PADDING_ORACLE", "INVALID_CURVE",  "WEAK_RSA_KEY", "SYMBOLIC_EXEC", 
                            "MODEL_CHECKING", "AI_FUZZING", "WEBSOCKET_INJECTION", "CVE_LOOKUP", "PARAM_EXTRACTOR", "CLEANUP",
                            "AST_ANALYSIS", "BINARY_ANALYSIS", "CSPM_ANALYSIS", "DEEP_403_AUDIT", "DEEP_RE_ATTACK", "ADVANCED_RECON",
                            "EXPLOIT_CHAINING", "AI_ENHANCED_FUZZ", "EXTERNAL_TOOLS", "AI_ORCHESTRATOR",
                        ].map(action => (
                            <label key={action} className="flex items-center gap-1.5 text-[9px] text-slate-400 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={selectedActions.includes(action)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedActions(prev => [...prev, action]);
                                        } else {
                                            setSelectedActions(prev => prev.filter(a => a !== action));
                                        }
                                    }}
                                    className="w-3 h-3 accent-cyan-500"
                                />
                                <span className="hover:text-cyan-300 transition-colors">{action.replace(/_/g, ' ')}</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
            
            <div className="mt-8 mb-8 bg-slate-950/40 border border-slate-900 rounded-xl p-5">
                <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-4">Scan Comparison</h3>
                <div className="flex gap-2 items-center">
                    <input
                        className="bg-black/60 border border-slate-900 p-2 rounded text-xs text-cyan-400 w-xl"
                        placeholder="Scan ID 1"
                        value={compareScanId1}
                        onChange={(e) => setCompareScanId1(e.target.value)}
                    />
                    <input
                        className="bg-black/60 border border-slate-900 p-2 rounded text-xs text-cyan-400 w-xl"
                        placeholder="Scan ID 2"
                        value={compareScanId2}
                        onChange={(e) => setCompareScanId2(e.target.value)}
                    />
                    <button
                        onClick={compareScans}
                        className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2 rounded text-xs font-bold uppercase text-black"
                    >
                        Compare
                    </button>
                </div>
                {compareResult && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div className="bg-red-950/10 border border-red-500/10 p-3 rounded">
                            <h4 className="text-red-400 font-bold mb-2">New Findings ({compareResult.new_findings.length})</h4>
                            {compareResult.new_findings.map((f, i) => (
                                <div key={i} className="text-slate-300">{f.title}</div>
                            ))}
                        </div>
                        <div className="bg-emerald-950/10 border border-emerald-500/10 p-3 rounded">
                            <h4 className="text-emerald-400 font-bold mb-2">Fixed ({compareResult.fixed_findings.length})</h4>
                            {compareResult.fixed_findings.map((f, i) => (
                                <div key={i} className="text-slate-300">{f.title}</div>
                            ))}
                        </div>
                        <div className="bg-slate-800/40 border border-slate-700 p-3 rounded">
                            <h4 className="text-slate-400 font-bold mb-2">Unchanged ({compareResult.unchanged.length})</h4>
                            {compareResult.unchanged.map((f, i) => (
                                <div key={i} className="text-slate-300">{f.title}</div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ─── INTELLIGENCE REPORT MATRIX LEDGER ─── */}
            <div className="bg-slate-950/40 border border-slate-900 rounded-2xl overflow-hidden shadow-2xl backdrop-blur-md">
                <div className="p-5 border-b border-slate-900 bg-slate-950/80 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <span className="text-[10px] font-bold text-cyan-500 uppercase tracking-widest flex items-center gap-2">
                        <Terminal size={14} className="text-cyan-600" /> FORENSIC RECONNAISSANCE ARCHIVES
                  </span>
                  <span className="text-[9px] font-mono font-bold bg-cyan-950/30 text-cyan-400 px-3 py-1.5 rounded border border-cyan-500/20 tracking-wider">
                        {reports.length} LEDGERS ENCRYPTED & SECURED
                  </span>
                </div>

                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[700px]">
                        <thead>
                            <tr className="text-[9px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-900/60 bg-black/40">
                                <th className="px-6 lg:px-4 py-4">Scan ID</th>
                                <th className="px-6 lg:px-4 py-4">Scan Time</th>
                                <th className="px-6 lg:px-4 py-4">Target Core Node</th>
                                <th className="px-6 lg:px-4 py-4 text-center">Threat Footprint</th>
                                <th className="px-6 lg:px-4 py-4">Secured At</th>
                                <th className="px-6 lg:px-4 py-4 text-right">Operations Hub</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-900/40">
                            {reports.map((report) => (
                                <tr key={report.id} className="hover:bg-cyan-950/10 transition-colors duration-200 group">
                                    <td className="px-2 lg:px-1 py-2 text-xs text-cyan-400 font-mono select-all">
                                        {report.id}
                                    </td>
                                    <td className="px-2 lg:px-1 py-2 text-[11px] text-slate-500">
                                        {report.finished_at
                                            ? `${Math.round((new Date(report.finished_at) - new Date(report.started_at)) / 1000)}s`
                                            : 'Running...'}
                                    </td>
                                    <td className="px-2 lg:px-1 py-2 font-bold text-slate-200 text-xs">
                                        <div className="flex items-center gap-3">
                                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/30 group-hover:bg-cyan-400 shadow-[0_0_6px_transparent] group-hover:shadow-cyan-400 transition-all duration-300" />
                                            <span className="tracking-wide select-all truncate max-w-[250px] md:max-w-[400px]">{report.target_url}</span>
                                        </div>
                                    </td>
                                    <td className="px-2 lg:px-1 py-2 text-center">
                                        <span className={`px-1 py-1 rounded text-[9px] font-bold tracking-widest uppercase border ${
                                            report.findings_count > 0 
                                                ? 'bg-red-950/20 border-red-500/30 text-red-400 filter drop-shadow-[0_0_2px_rgba(239,68,68,0.2)] animate-pulse' 
                                                : 'bg-emerald-950/20 border-emerald-500/30 text-emerald-400'
                                        }`}>
                                            {report.findings_count > 0 ? `EXPLOITS // ${report.findings_count}` : 'CLEAN_VERDICT'}
                                        </span>
                                    </td>
                                    <td className="px-2 lg:px-1 py-2 text-[11px] text-slate-500">
                                        {new Date(report.started_at).toLocaleString()}
                                    </td>
                                    <td className="px-2 lg:px-1 py-2 text-right">
                                        <div className="flex justify-end gap-2.5 opacity-80 group-hover:opacity-100 transition-opacity">
                                            <button 
                                                onClick={() => viewReportDetails(report.id)}
                                                className="p-2 bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 hover:bg-cyan-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-cyan-400"
                                                title="Inspect Dossier"
                                            >
                                                <Eye size={14} />
                                            </button>
                                            <button
                                                onClick={async () => {
                                                    setDownloading(report.id);
                                                    try {
                                                        // Dynamically fetch session token from storage
                                                        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                                                        const baseURL = axiosClient.defaults.baseURL || "";

                                                        // Adaptively resolve the API target domain to handle development port partitions (3000 vs 8000)
                                                        let apiEndpoint = `/api/v1/admin/web-audit/loot/${report.id}`;
                                                        if (baseURL.startsWith("http")) {
                                                            apiEndpoint = `${baseURL}/admin/web-audit/loot/${report.id}`;
                                                        } else if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
                                                            apiEndpoint = `http://localhost:8000/api/v1/admin/web-audit/loot/${report.id}`;
                                                        }

                                                        // Direct vanilla axios call with explicit SOCKS/API paths
                                                        const response = await axios.get(apiEndpoint, {
                                                            responseType: 'blob',
                                                            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                                                        });
                                                        
                                                        // Verify if response data is an HTML error fallback string
                                                        if (response.data && response.data.type === 'text/html') {
                                                            const text = await response.data.text();
                                                            throw new Error('Server returned an HTML error page: ' + text.slice(0, 200));
                                                        }

                                                        const blob = new Blob([response.data], { type: 'application/zip' });
                                                        const url = window.URL.createObjectURL(blob);
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.download = `loot_scan_${report.id}.zip`;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        link.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (error) {
                                                        // Extract error details safely by deserializing the error Blob
                                                        if (error.response?.data instanceof Blob) {
                                                            const reader = new FileReader();
                                                            reader.onload = () => {
                                                                try {
                                                                    const parsedError = JSON.parse(reader.result);
                                                                    alert(`Download failed: ${parsedError.detail || 'Server rejected the request.'}`);
                                                                } catch {
                                                                    // Extract and print the raw unparseable server response (e.g. raw HTML error or proxy status)
                                                                    const rawPayload = reader.result ? String(reader.result).slice(0, 180) : "Empty response";
                                                                    console.error("Raw server error payload:", reader.result);
                                                                    alert(`Download failed: ${rawPayload}`);
                                                                }
                                                            };
                                                            reader.readAsText(error.response.data);
                                                        } else {
                                                            const detail = error.response?.data?.detail || error.message || 'Download failed';
                                                            alert(`Download failed: ${detail}`);
                                                        }
                                                    } finally {
                                                        setDownloading(null);
                                                    }
                                                }}
                                                disabled={downloading === report.id}
                                                className={`p-2 bg-slate-900/60 border border-slate-800 hover:border-emerald-500/40 hover:bg-emerald-950/20 rounded-md transition-all duration-200 ${
                                                    downloading === report.id ? 'opacity-50 cursor-not-allowed' : 'text-slate-400 hover:text-emerald-400'
                                                }`}
                                                title="Download Evidence Loot"
                                            >
                                                {downloading === report.id ? (
                                                    <Loader size={14} className="animate-spin" />
                                                ) : (
                                                    <Archive size={14} />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => downloadClientPdf(report.id)}
                                                className="p-2 bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 hover:bg-cyan-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-cyan-400"
                                                title="Download Client Report (PDF)"
                                            >
                                                <FileText size={14} />
                                            </button>
                                            <button
                                                onClick={() => viewClientHtml(report.id)}
                                                className="p-2 bg-slate-900/60 border border-slate-800 hover:border-emerald-500/40 hover:bg-emerald-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-emerald-400"
                                                title="View Client Report (HTML)"
                                            >
                                                <FileCode size={14} />
                                            </button>
                                            <button
                                                onClick={() => viewClientDiff(report.id)}
                                                className="p-2 bg-slate-900/60 border border-slate-800 hover:border-amber-500/40 hover:bg-amber-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-amber-400"
                                                title="Before/After Diff Report"
                                            >
                                                <GitCompare size={14} />
                                            </button>

                                            <button 
                                                onClick={() => deleteReport(report.id)}
                                                className="p-2 bg-slate-900/60 border border-slate-800 hover:border-red-500/40 hover:bg-red-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-red-400"
                                                title="Purge Telemetry"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            
                                            {report.status === 'running' && (
                                                <>
                                                    <button
                                                        onClick={() => viewLiveLogs(report.id)}
                                                        className="p-2 bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 hover:bg-cyan-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-cyan-400"
                                                        title="View Live Logs"
                                                    >
                                                        <Radio size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => stopScan(report.id)}
                                                        className="p-2 bg-slate-900/60 border border-slate-800 hover:border-red-500/40 hover:bg-red-950/20 rounded-md transition-all duration-200 text-slate-400 hover:text-red-400"
                                                        title="Stop Scan"
                                                    >
                                                        <Square size={14} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {reports.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-8 py-12 text-center text-xs text-slate-600 italic">
                                        No telemetry records compiled within oculus archives.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ─── MODAL 1: LIVE SCANNING CONSOLE STREAM ─── */}
            {showLogModal && (
                <div className="fixed inset-0 wrongs-0 z-50 flex items-center justify-center p-4 backdrop-blur-xl bg-black/70 transition-all duration-300">
                    <div className="bg-[#040712] border border-cyan-500/30 w-full max-w-xl rounded-xl shadow-2xl overflow-hidden animate-modalIn relative max-h-[85vh] flex flex-col">
                        <div className="p-4 border-b border-slate-900 bg-cyan-950/10 flex items-center justify-between">
                            <h3 className="text-xs font-bold tracking-widest text-cyan-400 uppercase flex items-center gap-2">
                                <Cpu className="animate-spin-slow text-cyan-500" size={15} /> NEURAL RECON MATRIX STREAM
                            </h3>
                            <span className="text-[10px] text-slate-400 font-mono">
                                IPs rotated: {stealthIPs.length}
                            </span>
                            <span className="text-[10px] text-slate-400 font-mono">
                                Running: {Math.floor(elapsedTime / 60)}m {elapsedTime % 60}s
                            </span>
                            <button 
                                onClick={() => { setShowLogModal(false); clearInterval(pollingInterval); setIsScanning(false); }} 
                                className="text-slate-500 hover:text-white p-1 rounded hover:bg-slate-900 transition-colors"
                            >
                                <X size={16}/>
                            </button>
                        </div>
                        <div className="p-6 h-100 overflow-y-auto overflow-x-hidden bg-[#010308]/90 custom-scrollbar font-mono text-[11px] space-y-2.5">
                            {logs.map((log, i) => (
                                <div key={i} className="flex gap-3 animate-slideUp leading-relaxed min-w-0">
                                    <span className="text-cyan-500 select-none shrink-0">»</span>
                                    <span className={`flex-1 min-w-0 break-all whitespace-pre-wrap ${i === 0 ? "text-white font-bold" : "text-slate-500"}`}>
                                        {log}
                                    </span>
                                </div>
                            ))}
                            {isScanning && activeScanId && microAgentMode && (
                                <MicroAgentsPanel
                                    scanId={activeScanId}
                                    compact={true}
                                    scanStatus="running"
                                />
                            )}
                            <div className="w-1.5 h-3 bg-cyan-500 animate-pulse inline-block ml-6" />
                        </div>
                    </div>
                </div>
            )}

            {/* ─── MODAL 2: TABS DOSSIER INTERFACE ─── */}
            {showDetailsModal && selectedReport && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-6 backdrop-blur-xl bg-black/80 transition-all duration-300">
                    <div className="bg-[#030611] border border-slate-900 w-full max-w-8xl rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-slideInRight relative">
                        
                        {/* Dossier Dynamic Header */}
                        <div className="p-6 border-b border-slate-900 bg-slate-950/80 flex justify-between items-center gap-4">
                            <div>
                                <span className="text-[9px] text-cyan-400 font-bold uppercase tracking-widest block mb-0.5">// SOVEREIGN DISCOVERY REPORT MATRIX</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500">Scan ID:</span>
                                    <span className="text-[10px] text-cyan-400 font-mono font-bold select-all">{selectedReport.job?.id || selectedReport.id}</span>
                                </div>
                                <h2 className="text-base lg:text-lg font-black text-white tracking-wider break-all select-all">
                                    {selectedReport.job ? selectedReport.job.target_url : selectedReport.target_url}
                                </h2>
                            </div>
                           

                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={() => downloadClientPdf(selectedReport.job?.id || selectedReport.id)}
                                    className="p-2 bg-slate-900 border border-slate-800 hover:border-cyan-500/40 hover:bg-cyan-950/20 rounded-lg transition-all text-slate-400 hover:text-cyan-400 flex items-center gap-1.5"
                                    title="Download Client Report (PDF)"
                                >
                                    <FileText size={14} />
                                    <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">PDF</span>
                                </button>
                                <button
                                    onClick={() => viewClientHtml(selectedReport.job?.id || selectedReport.id)}
                                    className="p-2 bg-slate-900 border border-slate-800 hover:border-emerald-500/40 hover:bg-emerald-950/20 rounded-lg transition-all text-slate-400 hover:text-emerald-400 flex items-center gap-1.5"
                                    title="View Client Report (HTML)"
                                >
                                    <FileCode size={14} />
                                    <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">HTML</span>
                                </button>
                                <button
                                    onClick={() => viewClientDiff(selectedReport.job?.id || selectedReport.id)}
                                    className="p-2 bg-slate-900 border border-slate-800 hover:border-amber-500/40 hover:bg-amber-950/20 rounded-lg transition-all text-slate-400 hover:text-amber-400 flex items-center gap-1.5"
                                    title="Before/After Diff Report"
                                >
                                    <GitCompare size={14} />
                                    <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">DIFF</span>
                                </button>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="p-2 bg-slate-900 border border-slate-800 hover:border-red-500/40 hover:bg-red-950/10 rounded-lg transition-all text-slate-400 hover:text-red-400 shrink-0"
                                    title="Close"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Interactive Matrix Tabs Deck */}
                        <div className="flex border-b border-slate-900 bg-slate-950/40 p-1 text-[11px] font-mono">
                            {[
                                { key: 'recon', label: 'NETWORK_RECON', icon: <List size={12} /> },
                                { key: 'report', label: 'REPORT', icon: <FileText size={12} /> },
                                { key: 'findings', label: 'THREAT_FINDINGS', icon: <Bug size={12} /> },
                                { key: 'deep_audit', label: 'DEEP AUDIT', icon: <ShieldAlert size={12} /> },
                                { key: 'parameters', label: 'PARAMETERS & SCHEMA', icon: <Globe size={12} /> },
                                { key: 'profiler', label: 'ENDPOINT PROFILER', icon: <Globe size={12} /> },
                                { key: 'secrets', label: 'SENSITIVE DATA', icon: <ShieldAlert size={12} /> },
                                { key: 'modules', label: 'MODULE STATUS', icon: <Cpu size={12} /> },
                                { key: 'micro_agents', label: 'MICRO AGENTS', icon: <Activity size={12} /> },
                                { key: 'plan', label: 'REMEDIATION_PLAN', icon: <FileText size={12} /> },
                                
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center justify-center gap-1 px-5 py-2.5 rounded-lg font-bold tracking-widest uppercase transition-all duration-300 flex-1 sm:flex-none ${
                                        activeTab === tab.key 
                                            ? 'text-cyan-400 bg-cyan-950/20 border border-cyan-500/20 shadow-inner' 
                                            : 'text-slate-500 hover:text-slate-300'
                                    }`}
                                >
                                    {tab.icon} <span className="hidden sm:inline">{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Core Dossier Feed Layer */}
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-[#02040b]">
                            
                            {/* TAB 1: NETWORK RECON BLOCK */}
                            {activeTab === 'recon' && (
                                <div className="space-y-6">
                                    {/* Subdomains Layout Grid */}
                                    <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5">
                                        <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <span>» DETECTED SUBDOMAINS</span> 
                                            <span className="text-[10px] text-slate-600">({selectedReport.subdomains?.length || 0})</span>
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                                            {selectedReport.subdomains?.map((sub, i) => (
                                                <div key={i} className="bg-black/40 border border-slate-900/60 p-2.5 rounded-lg text-xs text-slate-400 tracking-wide select-all hover:border-slate-800 transition-colors">
                                                    {sub.subdomain}
                                                </div>
                                            ))}
                                            {(selectedReport.subdomains?.length === 0 || !selectedReport.subdomains) && (
                                                <div className="text-xs text-slate-600 italic col-span-2">No active subdomains mapped.</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Endpoints Stack Block */}
                                    <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5">
                                        <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <span>» COMPILED ENDPOINTS</span>
                                            <span className="text-[10px] text-slate-600">({selectedReport.endpoints?.length || 0})</span>
                                        </h3>
                                        <div className="space-y-1.5 max-h-56 overflow-y-auto custom-scrollbar pr-1">
                                            {selectedReport.endpoints?.map((ep, i) => (
                                                <div key={i} className="bg-black/40 border border-slate-900/60 p-3 rounded-lg flex justify-between items-center text-xs text-slate-400 select-all hover:border-slate-800 transition-colors">
                                                    <span className="truncate max-w-[70%] tracking-wide">{ep.url}</span>
                                                    <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase border ${
                                                        ep.method?.toUpperCase() === 'POST' ? 'bg-amber-950/30 text-amber-400 border-amber-500/20' : 'bg-cyan-950/30 text-cyan-400 border-cyan-500/20'
                                                    }`}>{ep.method || 'GET'}</span>
                                                </div>
                                            ))}
                                            {(selectedReport.endpoints?.length === 0 || !selectedReport.endpoints) && (
                                                <div className="text-xs text-slate-600 italic">No mapped endpoint vectors found.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Actions Executed Summary */}
                            <div className="mt-8 bg-slate-950/40 border border-slate-900 rounded-xl p-5 mb-5">
                                <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-4">⚡ Actions Executed</h3>
                                <div className="grid grid-cols-8 gap-2">
                                    {SAFE_ACTIONS_LIST.map(action => (
                                        <label key={action} className="flex items-center gap-1.5 text-[9px] text-slate-400 cursor-pointer select-none">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedActions.includes(action)} 
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedActions(prev => [...prev, action]);
                                                    } else {
                                                        setSelectedActions(prev => prev.filter(a => a !== action));
                                                    }
                                                }}
                                                className="w-3 h-3 accent-cyan-500"
                                            />
                                            <span className="hover:text-cyan-300 transition-colors">{action.replace(/_/g, ' ')}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {activeTab === 'modules' && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-4 mt-5 ">
                                    <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest">Module Execution Status</h3>
                                    <span className="text-[10px] text-slate-400 font-mono">
                                        Total IPs rotated: <span className="text-cyan-400 font-bold">{selectedReport.job?.ip_rotations || 0}</span>
                                    </span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-[10px]">
                                        <thead className="text-slate-500 uppercase tracking-widest border-b border-slate-800">
                                            <tr>
                                                <th className="p-2">Module</th>
                                                <th className="p-2">Status</th>
                                                <th className="p-2">Duration</th>
                                                <th className="p-2">Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {moduleLogs.map((log, i) => (
                                                <tr key={i} className="border-b border-slate-900">
                                                    <td className="p-2 text-slate-300">{log.module_name?.replace(/_/g, ' ')}</td>
                                                    <td className="p-2">
                                                        <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase ${
                                                            log.status === 'success' ? 'bg-emerald-950/20 text-emerald-400' : 'bg-red-950/20 text-red-400'
                                                        }`}>{log.status}</span>
                                                    </td>
                                                    <td className="p-2 text-slate-500">{log.duration_ms ? `${Math.round(log.duration_ms)}ms` : '-'}</td>
                                                    <td className="p-2 text-red-400 max-w-xs truncate" title={log.error_message}>{log.error_message || '-'}</td>
                                                </tr>
                                            ))}
                                            {moduleLogs.length === 0 && (
                                                <tr><td colSpan={4} className="text-center text-slate-600 py-4">No module logs found for this scan.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {activeTab === 'report' && (
                            <div className="space-y-4">
                                <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest">Scan Report</h3>
                                {!reportData ? (
                                    <div className="text-center text-slate-600 py-12 text-xs italic">Loading report...</div>
                                ) : (
                                    <>
                                        {/* Summary Stats */}
                                        <div className="grid grid-cols-5 gap-3">
                                            <div className="bg-slate-950/40 border border-slate-900 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-cyan-400">{reportData.findings?.length || 0}</div>
                                                <div className="text-[10px] text-slate-500">Total</div>
                                            </div>
                                            <div className="bg-red-950/20 border border-red-500/20 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-red-400">{reportData.findings?.filter(f => f.severity === 'critical').length || 0}</div>
                                                <div className="text-[10px] text-red-400">Critical</div>
                                            </div>
                                            <div className="bg-orange-950/20 border border-orange-500/20 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-orange-400">{reportData.findings?.filter(f => f.severity === 'high').length || 0}</div>
                                                <div className="text-[10px] text-orange-400">High</div>
                                            </div>
                                            <div className="bg-amber-950/20 border border-amber-500/20 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-amber-400">{reportData.findings?.filter(f => f.severity === 'medium').length || 0}</div>
                                                <div className="text-[10px] text-amber-400">Medium</div>
                                            </div>
                                            <div className="bg-blue-950/20 border border-blue-500/20 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-blue-400">{reportData.findings?.filter(f => f.severity === 'low').length || 0}</div>
                                                <div className="text-[10px] text-blue-400">Low</div>
                                            </div>
                                        </div>

                                        {/* Subdomains & Endpoints Count */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-slate-950/40 border border-slate-900 p-3 rounded-lg">
                                                <span className="text-[10px] text-slate-500">Subdomains</span>
                                                <div className="text-lg font-bold text-slate-200">{reportData.subdomains?.length || 0}</div>
                                            </div>
                                            <div className="bg-slate-950/40 border border-slate-900 p-3 rounded-lg">
                                                <span className="text-[10px] text-slate-500">Endpoints</span>
                                                <div className="text-lg font-bold text-slate-200">{reportData.endpoints?.length || 0}</div>
                                            </div>
                                        </div>

                                        {/* Client Report Download Buttons */}
                                        <div className="flex gap-3 mt-4 flex-wrap">
                                            <button
                                                onClick={() => downloadClientPdf(selectedReport.job?.id || selectedReport.id)}
                                                className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2 rounded text-xs font-bold text-black transition-colors flex items-center gap-2"
                                            >
                                                <FileText size={14} />
                                                Client Report (PDF)
                                            </button>
                                            <button
                                                onClick={() => viewClientHtml(selectedReport.job?.id || selectedReport.id)}
                                                className="bg-emerald-700 hover:bg-emerald-600 px-4 py-2 rounded text-xs font-bold text-white transition-colors flex items-center gap-2"
                                            >
                                                <FileCode size={14} />
                                                Client Report (HTML)
                                            </button>
                                            <button
                                                onClick={() => viewClientDiff(selectedReport.job?.id || selectedReport.id)}
                                                className="bg-amber-700 hover:bg-amber-600 px-4 py-2 rounded text-xs font-bold text-white transition-colors flex items-center gap-2"
                                            >
                                                <GitCompare size={14} />
                                                Before/After Diff
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* ─── FINDINGS TAB ─── */}
                        {activeTab === 'findings' && (
                            
                            <div className="space-y-4">
                                {!filteredFindings || filteredFindings.length === 0 ? (
                                    <div className="text-center text-slate-600 py-12 text-xs italic">No vulnerabilities compiled in current system.</div>
                                ) : (
                                    <>
                                        {/* ── Summary Counters ── */}
                                        <div className="flex flex-wrap gap-3 mb-4">
                                            {['critical', 'high', 'medium', 'low', 'info'].map(sev => {
                                                const count = filteredFindings.filter(f => f.severity === sev).length;
                                                if (count === 0) return null;
                                                const colors = {
                                                    critical: 'bg-red-950/40 text-red-400 border-red-500/30',
                                                    high: 'bg-orange-950/40 text-orange-400 border-orange-500/30',
                                                    medium: 'bg-amber-950/40 text-amber-400 border-amber-500/30',
                                                    low: 'bg-blue-950/40 text-blue-400 border-blue-500/30',
                                                    info: 'bg-slate-800/40 text-slate-400 border-slate-700/30',
                                                };
                                                return (
                                                    <span key={sev} className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase border ${colors[sev]}`}>
                                                        {sev}: {count}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* ── Filter Panel ── */}
                                        <div className="bg-slate-950/30 border border-slate-900 rounded-xl p-4 mb-4">
                                            <div className="flex flex-wrap items-center gap-3">
                                                {/* Search */}
                                                <div className="flex-1 min-w-[150px]">
                                                    <input
                                                        type="text"
                                                        placeholder="Search findings..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        className="w-full bg-black/60 border border-slate-800 rounded px-3 py-1.5 text-xs text-slate-300 placeholder:text-slate-600 focus:border-cyan-500 outline-none"
                                                    />
                                                </div>
                                                {/* Method */}
                                                <select
                                                    value={filterMethod}
                                                    onChange={(e) => setFilterMethod(e.target.value)}
                                                    className="bg-black/60 border border-slate-800 rounded px-2 py-1.5 text-xs text-slate-300"
                                                >
                                                    {availableMethods.map(m => (
                                                        <option key={m} value={m}>{m === 'all' ? 'All Methods' : m}</option>
                                                    ))}
                                                </select>
                                                {/* Status */}
                                                <select
                                                    value={filterStatus}
                                                    onChange={(e) => setFilterStatus(e.target.value)}
                                                    className="bg-black/60 border border-slate-800 rounded px-2 py-1.5 text-xs text-slate-300"
                                                >
                                                    {availableStatuses.map(s => (
                                                        <option key={s} value={s}>{s === 'all' ? 'All Status' : s}</option>
                                                    ))}
                                                </select>
                                                {/* Category */}
                                                <select
                                                    value={filterCategory}
                                                    onChange={(e) => setFilterCategory(e.target.value)}
                                                    className="bg-black/60 border border-slate-800 rounded px-2 py-1.5 text-xs text-slate-300"
                                                >
                                                    {availableCategories.map(c => (
                                                        <option key={c} value={c}>{c === 'all' ? 'All Categories' : c.replace(/_/g, ' ').toUpperCase()}</option>
                                                    ))}
                                                </select>
                                                {/* Severity */}
                                                <select
                                                    value={filterSeverity}
                                                    onChange={(e) => setFilterSeverity(e.target.value)}
                                                    className="bg-black/60 border border-slate-800 rounded px-2 py-1.5 text-xs text-slate-300"
                                                >
                                                    {availableSeverities.map(s => (
                                                        <option key={s} value={s}>{s === 'all' ? 'All Severity' : s.toUpperCase()}</option>
                                                    ))}
                                                </select>
                                                {/* Sensitive only toggle */}
                                                <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={filterSensitiveOnly}
                                                        onChange={(e) => setFilterSensitiveOnly(e.target.checked)}
                                                        className="accent-cyan-500 w-3.5 h-3.5"
                                                    />
                                                    Sensitive Only
                                                </label>
                                                {/* Clear filters */}
                                                <button
                                                    onClick={() => {
                                                        setSearchQuery('');
                                                        setFilterMethod('all');
                                                        setFilterStatus('all');
                                                        setFilterCategory('all');
                                                        setFilterSeverity('all');
                                                        setFilterSensitiveOnly(false);
                                                    }}
                                                    className="text-[10px] text-slate-500 hover:text-cyan-400 transition-colors"
                                                >
                                                    Clear Filters
                                                </button>
                                            </div>
                                            {/* Filtered count */}
                                            <div className="mt-2 text-[10px] text-slate-500">
                                                Showing {filteredFindings.length} of {filteredFindings.length} findings
                                            </div>
                                        </div>
    

                                        {/* ── Grouping Toggle ── */}
                                        <div className="flex items-center gap-4 mb-3">
                                            <span className="text-[9px] text-slate-500 uppercase tracking-widest">Group by:</span>
                                            <button
                                                onClick={() => setGroupBy('severity')}
                                                className={`text-[9px] px-3 py-1 rounded transition-colors ${
                                                    groupBy === 'severity' ? 'bg-cyan-950/40 text-cyan-400 border border-cyan-500/30' : 'text-slate-500 hover:text-slate-300'
                                                }`}
                                            >
                                                Severity
                                            </button>
                                            <button
                                                onClick={() => setGroupBy('category')}
                                                className={`text-[9px] px-3 py-1 rounded transition-colors ${
                                                    groupBy === 'category' ? 'bg-cyan-950/40 text-cyan-400 border border-cyan-500/30' : 'text-slate-500 hover:text-slate-300'
                                                }`}
                                            >
                                                Category
                                            </button>
                                        </div>

                                        {/* ── Grouped Findings ── */}
                                        {Object.entries(groupFindings(filteredFindings, groupBy)).map(([group, items]) => {
                                            const severityColor = {
                                                critical: 'border-red-500/20 bg-red-950/10',
                                                high: 'border-orange-500/20 bg-orange-950/10',
                                                medium: 'border-amber-500/20 bg-amber-950/10',
                                                low: 'border-blue-500/20 bg-blue-950/10',
                                                info: 'border-slate-700/20 bg-slate-950/10',
                                            }[group] || 'border-slate-700/20 bg-slate-950/10';

                                            const severityBadge = {
                                                critical: 'text-red-400 border-red-500/30',
                                                high: 'text-orange-400 border-orange-500/30',
                                                medium: 'text-amber-400 border-amber-500/30',
                                                low: 'text-blue-400 border-blue-500/30',
                                                info: 'text-slate-400 border-slate-700/30',
                                            }[group] || 'text-slate-400 border-slate-700/30';

                                            return (
                                                <div key={group} className={`border rounded-xl p-4 ${severityColor}`}>
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <span className={`text-xs font-bold uppercase tracking-widest ${severityBadge}`}>
                                                            {groupBy === 'severity' ? group : group.replace(/_/g, ' ')}
                                                        </span>
                                                        <span className="text-[10px] text-slate-500">({items.length})</span>
                                                    </div>
                                                    <div className="space-y-3">
                                                        {items.map((f, idx) => (
                                                            <div key={idx} className="bg-black/40 border border-slate-900/60 p-4 rounded-lg hover:border-slate-800 transition-all">
                                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                                    <div className="text-xs font-bold text-slate-200 tracking-wide">{f.title}</div>
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className={`text-[8px] px-2 py-0.5 rounded font-black border uppercase tracking-widest ${
                                                                            f.severity === 'critical' ? 'bg-red-950/40 text-red-400 border-red-500/30' :
                                                                            f.severity === 'high' ? 'bg-orange-950/40 text-orange-400 border-orange-500/30' :
                                                                            f.severity === 'medium' ? 'bg-amber-950/40 text-amber-400 border-amber-500/30' :
                                                                            'bg-slate-800/40 text-slate-400 border-slate-700/30'
                                                                        }`}>{f.severity}</span>
                                                                        <span className="text-[8px] bg-blue-950/30 text-blue-400 px-2 py-0.5 rounded font-bold uppercase border border-blue-500/20 tracking-wider">{f.category || 'general'}</span>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => toggleExpandFinding(`${group}-${idx}`)} // unique key
                                                                    className="text-[10px] text-cyan-500 hover:text-cyan-400 flex items-center gap-1.5 mt-2 font-bold tracking-wider uppercase transition-colors"
                                                                >
                                                                    {expandedFindings[`${group}-${idx}`] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                                    {expandedFindings[`${group}-${idx}`] ? 'CLOSE_TELEMETRY_DATA' : 'READ_FULL_DOSSIER'}
                                                                </button>
                                                                {expandedFindings[`${group}-${idx}`] && (
                                                                    <div className="mt-3 space-y-3 border-t border-slate-900/50 pt-3 animate-fadeIn">
                                                                        <div className="bg-red-950/10 border border-red-500/10 rounded-lg p-3 font-sans">
                                                                            <h4 className="text-[9px] font-black text-red-400 uppercase tracking-widest font-mono mb-1">IMPACT COEFFICIENT</h4>
                                                                            <p className="text-xs text-slate-400 leading-relaxed">{f.impact || f.description}</p>
                                                                        </div>
                                                                        {f.steps_to_reproduce && f.steps_to_reproduce.length > 0 && (
                                                                            <div>
                                                                                <h4 className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">FORENSIC EXECUTION VECTOR</h4>
                                                                                <ol className="space-y-1.5">
                                                                                    {f.steps_to_reproduce.map((step, si) => (
                                                                                        <li key={si} className="text-xs text-slate-400 flex gap-2">
                                                                                            <span className="text-cyan-600 font-bold">[{si+1}]</span>
                                                                                            <span className="font-mono">{step}</span>
                                                                                        </li>
                                                                                    ))}
                                                                                </ol>
                                                                            </div>
                                                                        )}
                                                                        {f.proof && Object.keys(f.proof).length > 0 && (
                                                                            <div>
                                                                                <h4 className="text-[9px] font-black text-cyan-500 uppercase tracking-widest mb-2">RAW EXPLOIT PAYLOAD (PROOF)</h4>
                                                                                <div className="bg-[#010308] border border-slate-900 rounded-lg p-3 font-mono text-xs text-cyan-400/80 overflow-x-auto custom-scrollbar select-all">
                                                                                    <pre className="text-left">{JSON.stringify(f.proof, null, 2)}</pre>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-slate-900/50">
                                                                            {f.ai_confidence != null && (
                                                                                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                                                                                    <span className="font-bold uppercase tracking-wider">AI EVAL_CONFIDENCE:</span>
                                                                                    <span className="text-cyan-500 font-bold font-mono">{f.ai_confidence}%</span>
                                                                                </div>
                                                                            )}
                                                                            {f.fix_suggestion && (
                                                                                <div className="p-2 bg-emerald-950/10 rounded-lg border border-emerald-500/10 flex items-start gap-2 sm:max-w-[70%] w-full">
                                                                                    <ShieldCheck size={14} className="text-emerald-500 shrink-0 mt-0.5" />
                                                                                    <span className="text-[10px] text-emerald-400 font-mono leading-relaxed"><b className="text-slate-500 uppercase text-[9px] mr-1">PATCH:</b> {f.fix_suggestion}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        )}

                            {activeTab === 'deep_audit' && (
                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold text-cyan-400 uppercase">Deep 403 Audit Findings</h3>
                                    {deepAuditData.length === 0 ? (
                                        <div className="text-center text-slate-600 py-12 text-xs italic">No deep audit findings.</div>
                                    ) : (
                                        deepAuditData.map((f, i) => (
                                            <div key={i} className="bg-slate-950/40 border border-slate-900 p-4 rounded-xl">
                                                <div className="flex justify-between">
                                                    <span className="text-xs font-bold text-slate-200">{f.title}</span>
                                                    <span className={`text-[8px] px-2 py-0.5 rounded font-black uppercase border ${
                                                        f.severity === 'critical' ? 'bg-red-950/40 text-red-400 border-red-500/30' : 
                                                        f.severity === 'high' ? 'bg-orange-950/40 text-orange-400 border-orange-500/30' :
                                                        'bg-amber-950/40 text-amber-400 border-amber-500/30'
                                                    }`}>{f.severity}</span>
                                                </div>
                                                <div className="text-xs text-slate-400 mt-2">{f.impact}</div>
                                                {f.proof && (
                                                    <details className="mt-2">
                                                        <summary className="text-[10px] text-cyan-400 cursor-pointer">View Proof</summary>
                                                        <pre className="text-[9px] mt-1 bg-black/40 p-2 rounded overflow-x-auto">
                                                            {JSON.stringify(f.proof, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                                {f.fix_suggestion && (
                                                    <div className="text-[10px] text-emerald-400 mt-2">Fix: {f.fix_suggestion}</div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === 'parameters' && (
                                <div className="space-y-4">
                                    <h3 className="text-xs font-bold text-cyan-400 uppercase tracking-widest">Endpoint Parameters & JSON Schemas</h3>
                                    {parametersData.length === 0 ? (
                                        <div className="text-center text-slate-600 py-12 text-xs italic">No parameter data found for this scan.</div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-xs border-collapse">
                                                <thead className="text-slate-500 border-b border-slate-800">
                                                    <tr>
                                                        <th className="p-2 text-left">Endpoint</th>
                                                        <th className="p-2 text-left">Parameter</th>
                                                        <th className="p-2 text-left">Type</th>
                                                        <th className="p-2 text-left">Data Type</th>
                                                        <th className="p-2 text-left">Sample Value</th>
                                                        <th className="p-2 text-left">Required</th>
                                                        <th className="p-2 text-left">JSON Schema</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parametersData.map((p, i) => (
                                                        <tr key={i} className="border-b border-slate-900 hover:bg-slate-900/20">
                                                            <td className="p-2 text-slate-300 truncate max-w-xs">{p.endpoint_url || p.endpoint_id}</td>
                                                            <td className="p-2 text-cyan-400">{p.parameter_name}</td>
                                                            <td className="p-2"><span className="px-2 py-0.5 bg-slate-800 rounded text-[9px]">{p.parameter_type}</span></td>
                                                            <td className="p-2"><span className="px-2 py-0.5 bg-slate-800 rounded text-[9px]">{p.data_type}</span></td>
                                                            <td className="p-2 text-slate-400 truncate max-w-xs">{p.sample_value || '—'}</td>
                                                            <td className="p-2">{p.required ? '✅' : '❌'}</td>
                                                            <td className="p-2">
                                                                {p.json_schema ? (
                                                                    <details>
                                                                        <summary className="cursor-pointer text-cyan-400">View Schema</summary>
                                                                        <pre className="text-[9px] mt-1 p-2 bg-black/40 rounded overflow-x-auto">{JSON.stringify(p.json_schema, null, 2)}</pre>
                                                                    </details>
                                                                ) : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'profiler' && (
                                <div className="space-y-4 mt-5">
                                    {!filteredFindings || filteredFindings.length === 0 ? (
                                        <div className="text-center text-slate-600 py-12 text-xs italic">No profiler data available.</div>
                                    ) : (
                                        <>
                                            {filteredFindings
                                                .filter(f => f.category === 'endpoint_profile')
                                                .map((f, idx) => {
                                                    const proof = f.proof || {};
                                                    return (
                                                        <div key={idx} className="bg-slate-950/40 border border-slate-900 p-5 rounded-xl hover:border-slate-800 transition-all">
                                                            <div className="flex justify-between items-start mb-3">
                                                                <span className="text-xs font-bold text-slate-200">{f.title}</span>
                                                                <span className="text-[10px] text-cyan-400 uppercase tracking-widest">PROFILE</span>
                                                            </div>
                                                            {/* Basic GET response */}
                                                            {proof.get_responses && proof.get_responses.length > 0 && (
                                                                <div className="mb-3">
                                                                    <h4 className="text-[9px] font-black text-cyan-500 uppercase mb-1">GET Responses</h4>
                                                                    {proof.get_responses.map((resp, i) => (
                                                                        <div key={i} className="bg-black/40 border border-slate-900 p-2 rounded mb-2 text-[10px] text-slate-400">
                                                                            <div className="flex justify-between">
                                                                                <span className="text-cyan-600 font-mono">{resp.status_code}</span>
                                                                                <span className="text-slate-500">{resp.content_type}</span>
                                                                            </div>
                                                                            <details className="mt-1">
                                                                                <summary className="cursor-pointer text-cyan-400">View Headers</summary>
                                                                                <pre className="text-[9px] mt-1">{JSON.stringify(resp.headers, null, 2)}</pre>
                                                                            </details>
                                                                            <details className="mt-1">
                                                                                <summary className="cursor-pointer text-cyan-400">View Body Snippet</summary>
                                                                                <pre className="text-[9px] mt-1 whitespace-pre-wrap">{resp.body_snippet}</pre>
                                                                            </details>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* POST responses */}
                                                            {proof.post_responses && proof.post_responses.length > 0 && (
                                                                <div className="mb-3">
                                                                    <h4 className="text-[9px] font-black text-pink-500 uppercase mb-1">POST Responses</h4>
                                                                    {proof.post_responses.map((resp, i) => (
                                                                        <div key={i} className="bg-black/40 border border-slate-900 p-2 rounded mb-2 text-[10px] text-slate-400">
                                                                            <div className="flex justify-between">
                                                                                <span className="text-pink-400 font-mono">{resp.status_code}</span>
                                                                                <span className="text-slate-500">{resp.content_type} | {resp.method}</span>
                                                                            </div>
                                                                            <details className="mt-1">
                                                                                <summary className="cursor-pointer text-pink-300">View Sent Body</summary>
                                                                                <pre className="text-[9px] mt-1">{JSON.stringify(resp.body, null, 2)}</pre>
                                                                            </details>
                                                                            <details className="mt-1">
                                                                                <summary className="cursor-pointer text-pink-300">View Headers</summary>
                                                                                <pre className="text-[9px] mt-1">{JSON.stringify(resp.headers, null, 2)}</pre>
                                                                            </details>
                                                                            <details className="mt-1">
                                                                                <summary className="cursor-pointer text-pink-300">View Body Snippet</summary>
                                                                                <pre className="text-[9px] mt-1 whitespace-pre-wrap">{resp.body_snippet}</pre>
                                                                            </details>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* Discovered Params */}
                                                            {proof.discovered_params && proof.discovered_params.length > 0 && (
                                                                <div className="mb-2">
                                                                    <span className="text-[9px] font-black text-slate-500 uppercase">Discovered Params: </span>
                                                                    <span className="text-[10px] text-slate-400">{proof.discovered_params.join(', ')}</span>
                                                                </div>
                                                            )}
                                                            {/* Secrets Found */}
                                                            {proof.secrets_found && proof.secrets_found.length > 0 && (
                                                                <div className="bg-red-950/10 border border-red-500/10 rounded p-2">
                                                                    <span className="text-[9px] font-black text-red-400 uppercase">⚠ Secrets Found:</span>
                                                                    {proof.secrets_found.map((secret, si) => (
                                                                        <code key={si} className="block text-[9px] text-red-300 mt-1">{secret}</code>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* No data */}
                                                            {(!proof.get_responses || proof.get_responses.length === 0) &&
                                                            (!proof.post_responses || proof.post_responses.length === 0) && (
                                                                <div className="text-[10px] text-slate-600 italic">No response data captured.</div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                        </>
                                    )}
                                </div>
                            )}

                            {activeTab === 'secrets' && (
                            <div className="space-y-4">
                                {!filteredFindings || filteredFindings.length === 0 ? (
                                    <div className="text-center text-slate-600 py-12 text-xs italic">No sensitive data found.</div>
                                ) : (
                                    <>
                                        {filteredFindings
                                            .filter(f => {
                                                // Include if category is secret/pii or title contains secret/token/api_key
                                                const cat = (f.category || '').toLowerCase();
                                                const title = (f.title || '').toLowerCase();
                                                const proofStr = JSON.stringify(f.proof || {}).toLowerCase();
                                                return cat.includes('information_disclosure') ||
                                                    cat.includes('pii_leak') ||
                                                    cat.includes('secret') ||
                                                    title.includes('secret') ||
                                                    title.includes('token') ||
                                                    title.includes('api key') ||
                                                    proofStr.includes('secret') ||
                                                    proofStr.includes('token') ||
                                                    proofStr.includes('api_key');
                                            })
                                            .map((f, idx) => {
                                                const proof = f.proof || {};
                                                return (
                                                    <div key={idx} className="bg-slate-950/40 border border-slate-900 p-5 rounded-xl hover:border-slate-800 transition-all">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <span className="text-xs font-bold text-slate-200">{f.title}</span>
                                                            <span className={`text-[8px] px-2 py-0.5 rounded font-black uppercase tracking-widest ${
                                                                f.severity === 'critical' ? 'bg-red-950/40 text-red-400 border border-red-500/30' :
                                                                f.severity === 'high' ? 'bg-orange-950/40 text-orange-400 border border-orange-500/30' :
                                                                'bg-amber-950/40 text-amber-400 border border-amber-500/30'
                                                            }`}>{f.severity}</span>
                                                        </div>
                                                        <div className="bg-red-950/10 border border-red-500/10 rounded-lg p-3 mb-3">
                                                            <span className="text-[9px] font-black text-red-400 uppercase">⚠ Leaked Data</span>
                                                            <pre className="text-[10px] text-red-300 mt-2 font-mono whitespace-pre-wrap">
                                                                {JSON.stringify(proof, null, 2)}
                                                            </pre>
                                                        </div>
                                                        <div className="text-[10px] text-slate-400">
                                                            <span className="font-bold">Fix: </span>{f.fix_suggestion}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </>
                                )}
                            </div>
                        )}

                        {/* MICRO AGENTS TAB */}
                        {activeTab === 'micro_agents' && (
                            <MicroAgentsPanel
                                scanId={selectedReport.job?.id || selectedReport.id}
                                compact={false}
                                scanStatus={selectedReport.job?.status || 'completed'}
                            />
                        )}

                        {/* MASTER REMEDIATION PIPELINE BLOCK */}
                            {activeTab === 'plan' && (
                                <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5 mt-5 relative shadow-md">
                                    <h3 className="text-xs font-black text-cyan-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <ShieldCheck size={14} className="text-emerald-500" /> MASTER REMEDIATION PIPELINE SCRIPT
                                    </h3>
                                    <div className="space-y-2 select-all max-h-96 overflow-y-auto custom-scrollbar pr-1">
                                        {filteredFindings?.map((f, idx) => (
                                            <div key={idx} className="flex gap-3.5 items-start p-3.5 bg-black/40 rounded-lg border border-slate-900 hover:border-slate-800 transition-colors">
                                                <span className="text-cyan-600 font-mono font-bold text-xs mt-0.5">[{idx+1}]</span>
                                                <p className="text-xs text-slate-300 leading-relaxed font-mono">{f.fix_suggestion}</p>
                                            </div>
                                        ))}
                                        {(!filteredFindings || filteredFindings.length === 0) && (
                                            <p className="text-xs text-slate-600 italic">No instructions available for injection.</p>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            )}

            {/* Core Responsive Asset Animators Matrix */}
            <style>{`
                .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
                .animate-modalIn { animation: modalPop 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .animate-slideInRight { animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .animate-slideUp { animation: slideUp 0.2s ease-out forwards; }
                .animate-spin-slow { animation: spin 12s linear infinite; }
                
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes modalPop { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

                .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.01); }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.2); border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(6,182,212,0.4); }
            `}</style>
        </div>
    );
};

export default WebAuditPage;